import { inject, Injectable } from '@angular/core'
import { PositionDetail } from '@ftr/annotations-contracts'
import { Uuid } from '@ftr/contracts/type/shared'
import { RemoteData } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs'
import { InternalPositionsService } from '../../services'
import {
  AddInternalPositionCommand,
  DeleteInternalPositionCommand,
  FetchInternalPositionsCommand,
  InitialiseInternalPositionsListCommand,
  OrderInternalPositionsCommand,
  PositionInvolvement,
  UpdateInternalPositionCommand,
} from './internal-positions.commands'
import {
  CourtSystemWithInvolvementKey,
  InternalPositionsListInstanceStateModel,
  InternalPositionsListStateModel,
} from './internal-positions.model'

export function defaultInternalPositionsListState(): InternalPositionsListStateModel {
  return { instances: {} }
}

@State<InternalPositionsListStateModel>({
  name: 'internalPositionsState',
  defaults: defaultInternalPositionsListState(),
})
@Injectable()
export class InternalPositionsState {
  private readonly internalPositionsService = inject(InternalPositionsService)

  @Selector()
  static allInstanceStates(
    state: InternalPositionsListStateModel,
  ): Record<Uuid, InternalPositionsListInstanceStateModel> {
    return state?.instances ?? {}
  }

  static readonly internalPositions = memoize(
    (
      courtSystemId: string,
      involvement: PositionInvolvement,
    ): ((sourceStates: ReturnType<typeof InternalPositionsState.allInstanceStates>) => PositionDetail[]) =>
      createSelector([InternalPositionsState.allInstanceStates], sourceStates => {
        const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)
        return sourceStates[key]?.internalPositions ?? []
      }),
    (courtSystemId, involvement) => `${courtSystemId}_${involvement}`,
  )

  static readonly status = memoize(
    (
      courtSystemId: string,
      involvement: PositionInvolvement,
    ): ((sourceStates: ReturnType<typeof InternalPositionsState.allInstanceStates>) => RemoteData<null>) =>
      createSelector([InternalPositionsState.allInstanceStates], sourceStates => {
        const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)
        return sourceStates[key]?.status ?? RemoteData.notAsked()
      }),
    (courtSystemId, involvement) => `${courtSystemId}_${involvement}`,
  )

  @Action(InitialiseInternalPositionsListCommand)
  private initialiseInternalPositionsList(
    { setState, getState, dispatch }: StateContext<InternalPositionsListStateModel>,
    { courtSystemId, involvement }: InitialiseInternalPositionsListCommand,
  ): Observable<void> | undefined {
    const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)
    const instance = getState().instances[key]
    if (instance?.status.isSuccess() || instance?.status.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(key, {
        internalPositions: [],
        status: RemoteData.notAsked(),
      }),
    )

    return dispatch(new FetchInternalPositionsCommand(courtSystemId, involvement))
  }

  @Action(OrderInternalPositionsCommand)
  private async orderInternalPositionsCommand(
    { getState, setState }: StateContext<InternalPositionsListStateModel>,
    { courtSystemId, involvement, internalPositions }: OrderInternalPositionsCommand,
  ): Promise<void> {
    const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)
    const instance = getState().instances[key]
    const existingPositions = instance?.internalPositions ?? []
    const existingProfilesCountById = new Map(existingPositions.map(position => [position.id, position.profilesCount]))

    const mergeProfilesCount = (positions: PositionDetail[]): PositionDetail[] =>
      positions.map(position => {
        const existingProfilesCount = existingProfilesCountById.get(position.id)
        if (existingProfilesCount === undefined) return position
        return { ...position, profilesCount: existingProfilesCount }
      })

    const updatedPositions = internalPositions.map((role, index) => ({ ...role, sequenceNumber: index + 1 }))

    try {
      const result = await lastValueFrom(
        this.internalPositionsService.orderInternalPositions(updatedPositions, courtSystemId),
      )
      if (result.isFailure()) {
        setState(
          makeInstancePatch(key, {
            status: RemoteData.failure(
              new Error('Failed to update internal positions', { cause: result._error.message }),
            ),
          }),
        )
        return
      }

      const sortedPositions =
        result.get()?.sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0)) ?? updatedPositions
      setState(
        makeInstancePatch(key, {
          internalPositions: mergeProfilesCount(sortedPositions),
          status: RemoteData.success(null),
        }),
      )
    } catch (err) {
      setState(
        makeInstancePatch(key, {
          status: RemoteData.failure(new Error('Failed to update judge positions', { cause: err })),
        }),
      )
    }
  }

  @Action(FetchInternalPositionsCommand)
  private async fetchInternalPositions(
    { getState, setState }: StateContext<InternalPositionsListStateModel>,
    { courtSystemId, involvement }: FetchInternalPositionsCommand,
  ): Promise<void> {
    const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)
    const instance = getState().instances[key]
    if (instance?.status.isLoading()) {
      return
    }
    setState(
      makeInstancePatch(key, {
        status: RemoteData.loading(),
      }),
    )

    try {
      const result = await firstValueFrom<RemoteData<PositionDetail[]>>(
        this.internalPositionsService.getInternalPositions(courtSystemId, involvement),
      )

      if (result.isFailure()) {
        setState(
          makeInstancePatch(key, {
            internalPositions: [],
            status: RemoteData.failure(
              new Error('Failed to fetch internal positions', { cause: result._error.message }),
            ),
          }),
        )
        return
      }

      const positions = [...(result.get() ?? [])]
      setState(
        makeInstancePatch(key, {
          internalPositions:
            positions.sort(
              (positionA, positionB) => (positionA.sequenceNumber ?? 0) - (positionB.sequenceNumber ?? 0),
            ) ?? [],
          status: RemoteData.success(null),
        }),
      )
    } catch (err) {
      setState(
        makeInstancePatch(key, {
          internalPositions: [],
          status: RemoteData.failure(new Error('Failed to fetch internal positions', { cause: err })),
        }),
      )
    }
  }

  @Action(AddInternalPositionCommand)
  private async addInternalPosition(
    { dispatch, setState }: StateContext<InternalPositionsListStateModel>,
    { courtSystemId, involvement, positionName }: AddInternalPositionCommand,
  ): Promise<void> {
    const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)

    setState(
      makeInstancePatch(key, {
        status: RemoteData.loading(),
      }),
    )

    const result = await lastValueFrom(
      this.internalPositionsService.createInternalPosition({
        courtSystemId,
        name: positionName,
        involvement,
      }),
    )
    if (result.isFailure()) {
      setState(
        makeInstancePatch(key, {
          status: RemoteData.failure(new Error(result._error.message)),
        }),
      )
      dispatch(new FetchInternalPositionsCommand(courtSystemId, involvement))
      return
    }
    setState(
      makeInstancePatch(key, {
        status: RemoteData.success(null),
      }),
    )
    dispatch(new FetchInternalPositionsCommand(courtSystemId, involvement))
  }

  @Action(UpdateInternalPositionCommand)
  private async updateInternalPosition(
    { dispatch, setState }: StateContext<InternalPositionsListStateModel>,
    { courtSystemId, involvement, position }: UpdateInternalPositionCommand,
  ): Promise<void> {
    const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)

    setState(
      makeInstancePatch(key, {
        status: RemoteData.loading(),
      }),
    )

    const result = await lastValueFrom(
      this.internalPositionsService.updateInternalPosition(position.id, position.name, courtSystemId),
    )
    if (result.isFailure()) {
      setState(
        makeInstancePatch(key, {
          status: RemoteData.failure(new Error(result._error.message)),
        }),
      )
      dispatch(new FetchInternalPositionsCommand(courtSystemId, involvement))
      return
    }
    setState(
      makeInstancePatch(key, {
        status: RemoteData.success(null),
      }),
    )
    dispatch(new FetchInternalPositionsCommand(courtSystemId, involvement))
  }

  @Action(DeleteInternalPositionCommand)
  private async deleteInternalPosition(
    { dispatch, setState }: StateContext<InternalPositionsListStateModel>,
    { courtSystemId, involvement, positionId }: DeleteInternalPositionCommand,
  ): Promise<void> {
    const key = InternalPositionsState.getInstanceKey(courtSystemId, involvement)

    setState(
      makeInstancePatch(key, {
        status: RemoteData.loading(),
      }),
    )

    const result = await lastValueFrom(this.internalPositionsService.deleteInternalPosition(positionId, courtSystemId))
    if (result.isFailure()) {
      setState(
        makeInstancePatch(key, {
          status: RemoteData.failure(result._error),
        }),
      )

      return
    }

    setState(
      makeInstancePatch(key, {
        status: RemoteData.success(null),
      }),
    )
    dispatch(new FetchInternalPositionsCommand(courtSystemId, involvement))
  }

  private static getInstanceKey(
    courtSystemId: Uuid,
    involvement: PositionDetail['involvement'],
  ): CourtSystemWithInvolvementKey {
    return `${courtSystemId}-${involvement}`
  }
}

function makeInstancePatch(
  instanceId: CourtSystemWithInvolvementKey,
  instancePartial: Partial<InternalPositionsListInstanceStateModel>,
): StateOperator<InternalPositionsListStateModel> {
  return patch({
    instances: patch({
      [instanceId]: patch<InternalPositionsListInstanceStateModel>(instancePartial),
    }),
  })
}
